/**
 * UX-1: Global mutation error surface.
 *
 * extractMutationErrorMessage converts any thrown value into a human-readable
 * string suitable for display in a toast notification.
 *
 * Rules:
 *  - Plain Error with a non-empty message → use message, UNLESS the message is
 *    a raw ApiClientError kind token ("ApiClientError: http" / "ApiClientError:
 *    network" / "ApiClientError: parse") which conveys no user-useful info.
 *  - Everything else → generic fallback.
 *
 * The CB frontend's apiFetch throws plain Error objects with messages of the
 * form "API <status>: <body>", so those pass through as-is and are readable.
 */

import { format, parseISO } from 'date-fns';
import { ApiHttpError } from './api';
import { friendlyErrorMessage } from './errorMessages';

const FALLBACK = 'Something went wrong. Please try again.';

/** Matches the raw kind token emitted by ApiClientError's super() call. */
const API_CLIENT_RAW_KIND_RE = /^ApiClientError:\s*(http|network|parse)$/;

/**
 * GH #108 (part 1): the BE rejects a premature "Mark complete" with a 422 whose
 * message embeds a raw ISO timestamp, e.g.
 *   "Scheduled work continues until 2026-06-15T22:59:59.000Z"
 * Planners can't read that. Detect any ISO timestamp in the message and rewrite
 * it as a friendly "HH:mm 'on' do MMMM yyyy" (e.g. "23:59 on 15th June 2026").
 *
 * Applied to BOTH the global toast (here) and the inline complete-phase error
 * text in JobDetailPanel, so the planner always sees the friendly form.
 */
const ISO_TIMESTAMP_RE = /(\d{4}-\d{2}-\d{2}T[\d:.]+Z)/g;

export function friendlyifyIsoTimestamps(message: string): string {
  return message.replace(ISO_TIMESTAMP_RE, (iso) => {
    try {
      return format(parseISO(iso), "HH:mm 'on' do MMMM yyyy");
    } catch {
      return iso;
    }
  });
}

export function extractMutationErrorMessage(error: unknown): string {
  // Slice D: structured API errors flow through the central code→copy resolver,
  // so the global toast shows friendly text instead of a raw `API NNN: {…}`
  // body. friendlyifyIsoTimestamps still runs so dynamic transition messages
  // (e.g. "Scheduled work continues until <ISO>") read as a friendly date.
  if (error instanceof ApiHttpError) {
    return friendlyifyIsoTimestamps(friendlyErrorMessage(error));
  }
  if (error instanceof Error && error.message) {
    // Suppress raw kind tokens — they are internal identifiers, not user text.
    if (API_CLIENT_RAW_KIND_RE.test(error.message)) {
      return FALLBACK;
    }
    return friendlyifyIsoTimestamps(error.message);
  }
  return FALLBACK;
}
